/**
 * @file consent-texts-draft.ts
 * @description Черновик новой редакции текстов согласия — в браузере
 * (phase19.16, Р3г; решение оркестратора 2026-10-23, uiux §8 п. 5:
 * серверный черновик отклонён, черновик живёт в localStorage; «Правки не
 * сохранены» при уходе). Один черновик на вид в этом браузере.
 *
 * Хранилище — недоверенные данные: битый JSON, чужая форма или тексты без
 * нужных полей отбрасываются (черновика «нет»), а не ломают форму. Тексты
 * собираются заново по полям вида (`consentTextPaths`): лишние ключи из
 * хранилища в форму и на сервер не попадают (ревью М3).
 */

import type { ConsentTextKind, ConsentTextsByKind } from "@chaiberi/shared";

import { consentTextPaths, getTextAt, setTextAt } from "./consent-texts-form";

/** Версия формата записи — меняется при несовместимой правке. */
const DRAFT_FORMAT = 1;

export interface ConsentTextsDraft<K extends ConsentTextKind = ConsentTextKind> {
  kind: K;
  /** Действующая редакция, от которой начали (уходит в `expectedCurrent`). */
  expectedCurrent: number;
  /** Редакция-основа («Взять за основу» ред. N); обычно = expectedCurrent. */
  baseNumber: number;
  texts: ConsentTextsByKind[K];
  /** ISO-время сохранения. */
  savedAt: string;
  /** Кто сохранил — имя для строки «Черновик · Анна · 5 мин назад». */
  authorName: string;
}

export function consentDraftKey(kind: ConsentTextKind): string {
  return `commora.platform.consent-texts.draft:${kind}`;
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    // Доступ к хранилищу запрещён настройками браузера — черновика просто нет.
    return null;
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/** Строка по пути или undefined. */
function stringAt(root: unknown, path: string): string | undefined {
  let node: unknown = root;
  for (const key of path.split(".")) node = isRecord(node) ? node[key] : undefined;
  return typeof node === "string" ? node : undefined;
}

/**
 * Тексты вида только из его полей; нет поля или не строка — null.
 * Приведение типа безопасно: каждое поле вида проверено и записано.
 */
function pickTexts<K extends ConsentTextKind>(kind: K, raw: unknown): ConsentTextsByKind[K] | null {
  let out: object = {};
  for (const path of consentTextPaths(kind)) {
    const value = stringAt(raw, path);
    if (value === undefined) return null;
    out = setTextAt(out, path, value);
  }
  return out as ConsentTextsByKind[K];
}

/** Прочитать черновик вида; нет или битый — null. */
export function loadConsentDraft<K extends ConsentTextKind>(kind: K): ConsentTextsDraft<K> | null {
  const raw = storage()?.getItem(consentDraftKey(kind));
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed.format !== DRAFT_FORMAT || parsed.kind !== kind) return null;
  const { expectedCurrent, baseNumber, savedAt, authorName, texts } = parsed;
  if (typeof expectedCurrent !== "number" || typeof baseNumber !== "number") return null;
  if (typeof savedAt !== "string" || typeof authorName !== "string") return null;
  const picked = pickTexts(kind, texts);
  if (!picked) return null;
  return { kind, expectedCurrent, baseNumber, savedAt, authorName, texts: picked };
}

/** Сохранить черновик; false — хранилище недоступно или переполнено. */
export function saveConsentDraft(draft: ConsentTextsDraft): boolean {
  const store = storage();
  if (!store) return false;
  try {
    store.setItem(consentDraftKey(draft.kind), JSON.stringify({ format: DRAFT_FORMAT, ...draft }));
    return true;
  } catch {
    return false;
  }
}

export function clearConsentDraft(kind: ConsentTextKind): void {
  storage()?.removeItem(consentDraftKey(kind));
}

/** Черновик отличается от текстов (сравнение по полям вида). */
export function draftDiffers(kind: ConsentTextKind, a: object, b: object): boolean {
  return consentTextPaths(kind).some((path) => getTextAt(a, path) !== getTextAt(b, path));
}
